import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../utils/cn';
import { servicesApi } from '../../services/api';

const API_BASE = '/api';

// ===================== 类型定义 =====================
interface ContactMessage {
  id: number;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  status: 'unread' | 'read' | 'replied';
  created_at: string;
}

interface Project {
  id: number;
  slug: string;
  name_zh: string;
  name_vi: string;
  description_zh: string;
  description_vi: string;
  location_zh: string;
  location_vi: string;
  project_type: string;
  area_size: number | null;
  project_value: number | null;
  cover_image_url: string;
  status: string;
  is_featured: number | boolean;
  view_count: number;
  created_at: string;
}

interface NewProjectForm {
  name_zh: string;
  name_vi: string;
  description_zh: string;
  description_vi: string;
  location_zh: string;
  location_vi: string;
  project_type: string;
  area_size: string;
  cover_image_url: string;
  status: string;
  is_featured: boolean;
}

interface BranchOffice {
  id: number;
  city_zh: string;
  city_vi: string;
  address_zh: string;
  address_vi: string;
  phone: string;
  email: string;
  sort_order: number;
  is_active: number;
}

interface BranchOfficeForm {
  city_zh: string;
  city_vi: string;
  address_zh: string;
  address_vi: string;
  phone: string;
  email: string;
  sort_order: number;
  is_active: boolean;
}

interface Service {
  id: number;
  slug: string;
  name_zh: string;
  name_vi: string;
  icon_class: string;
  icon_color: string;
  description_zh: string;
  description_vi: string;
  image_url: string;
  sort_order: number;
  is_active: number;
}

// ===================== 首页Banner类型 =====================
interface HomepageBanner {
  id: number;
  section_key: string;
  title_zh: string;
  title_vi: string;
  content_zh: string;
  content_vi: string;
  image_url: string;
  image_alt_zh?: string;
  image_alt_vi?: string;
  button_text_zh?: string;
  button_text_vi?: string;
  button_link?: string;
  sort_order: number;
  is_active: boolean;
}

interface BannerFormData {
  id?: number;
  section_key: string;
  title_zh: string;
  title_vi: string;
  content_zh: string;
  content_vi: string;
  image_url: string;
  button_text_zh: string;
  button_text_vi: string;
  sort_order: number;
  is_active: boolean;
}

interface ServiceForm {
  slug: string;
  name_zh: string;
  name_vi: string;
  icon_class: string;
  icon_color: string;
  description_zh: string;
  description_vi: string;
  details_zh: string;
  details_vi: string;
  image_url: string;
  sort_order: number;
  is_active: boolean;
}

const emptyBranchForm: BranchOfficeForm = {
  city_zh: '',
  city_vi: '',
  address_zh: '',
  address_vi: '',
  phone: '',
  email: '',
  sort_order: 0,
  is_active: true,
};

const emptyProjectForm: NewProjectForm = {
  name_zh: '',
  name_vi: '',
  description_zh: '',
  description_vi: '',
  location_zh: '',
  location_vi: '',
  project_type: '',
  area_size: '',
  cover_image_url: '',
  status: 'completed',
  is_featured: false,
};

const PROJECT_TYPES: Record<string, string> = {
  electrical: '电气工程',
  hvac: '暖通空调',
  ventilation: '通风系统',
  decoration: '建筑装饰',
  waterproofing: '防水地坪',
  renovation: '综合工程',
};

// ===================== API 工具函数 =====================
function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('adminToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { ...getAuthHeaders(), ...(options?.headers || {}) },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || '请求失败');
  return data as T;
}

// ===================== 主组件 =====================
const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'messages' | 'projects' | 'settings' | 'branches' | 'services' | 'homepage'>('dashboard');

  // 数据状态
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [websiteSettings, setWebsiteSettings] = useState<Record<string, string>>({});
  const [branchOffices, setBranchOffices] = useState<BranchOffice[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  // 修改密码状态
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  // 分公司表单状态
  const [branchForm, setBranchForm] = useState<BranchOfficeForm>(emptyBranchForm);
  const [editingBranch, setEditingBranch] = useState<BranchOffice | null>(null);

  // 服务表单状态
  const [serviceForm, setServiceForm] = useState<ServiceForm>({
    slug: '',
    name_zh: '',
    name_vi: '',
    icon_class: 'fa fa-cog',
    icon_color: '#1890ff',
    description_zh: '',
    description_vi: '',
    details_zh: '[]',
    details_vi: '[]',
    image_url: '',
    sort_order: 0,
    is_active: true,
  });
  const [editingService, setEditingService] = useState<Service | null>(null);

  // 首页Banner状态
  const [banners, setBanners] = useState<HomepageBanner[]>([]);
  const [bannerForm, setBannerForm] = useState<BannerFormData>({
    section_key: '',
    title_zh: '',
    title_vi: '',
    content_zh: '',
    content_vi: '',
    image_url: '',
    button_text_zh: '',
    button_text_vi: '',
    sort_order: 0,
    is_active: true,
  });
  const [editingBanner, setEditingBanner] = useState<HomepageBanner | null>(null);
  // 首页子tab状态（公司简介/优势亮点）
  const [homepageSubTab, setHomepageSubTab] = useState<'banners' | 'company_intro' | 'advantages'>('banners');
  const [homepageSectionData, setHomepageSectionData] = useState<any>({});
  const [introFeaturesText, setIntroFeaturesText] = useState('');
  const [advantagesText, setAdvantagesText] = useState('');
  const [statsText, setStatsText] = useState('');

  // 弹窗状态
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProject, setEditingProject] = useState<NewProjectForm & { id: number } | null>(null);
  const [newProject, setNewProject] = useState<NewProjectForm>(emptyProjectForm);
  
  // 消息详情弹窗
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // 检查登录状态
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) navigate('/admin');
  }, [navigate]);

  // 加载数据
  const loadProjects = useCallback(async () => {
    try {
      const res = await apiFetch<{ success: boolean; data: Project[] }>('/content/projects/all');
      setProjects(res.data || []);
    } catch {
      // 加载失败时保持现有数据
    }
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      const res = await apiFetch<{ success: boolean; data: { messages: ContactMessage[] } | ContactMessage[] }>('/contacts/messages');
      // API返回的data可能是 { messages: [...], stats, pagination } 对象，也可能是直接数组
      const msgs = Array.isArray(res.data) ? res.data : (res.data?.messages || []);
      setMessages(msgs);
    } catch {
      // 加载失败时保持现有数据
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const res = await apiFetch<{ success: boolean; data: Record<string, string> }>('/settings/website');
      setWebsiteSettings(res.data || {});
    } catch {
      // 加载失败时保持现有数据
    }
  }, []);

  // 分公司操作
  const loadBranches = useCallback(async () => {
    try {
      const res = await apiFetch<{ success: boolean; data: BranchOffice[] }>('/branch-offices');
      setBranchOffices(res.data || []);
    } catch {
      // 加载失败时保持现有数据
    }
  }, []);

  // 服务操作
  const loadServices = useCallback(async () => {
    try {
      const res = await apiFetch<{ success: boolean; data: Service[] }>('/services');
      setServices(res.data || []);
    } catch {
      // 加载失败时保持现有数据
    }
  }, []);

  // 首页Banner操作
  const loadBanners = useCallback(async () => {
    try {
      const res = await apiFetch<{ success: boolean; data: HomepageBanner[] }>('/content/banners/all');
      setBanners(res.data || []);
    } catch {
      // 加载失败时保持现有数据
    }
  }, []);

  const handleSaveBanner = async (form: BannerFormData, id?: number) => {
    setActionLoading(true);
    try {
      const payload = { ...form, is_active: form.is_active ? 1 : 0 };
      if (id) {
        await apiFetch(`/content/banners/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
        showToast('Banner已更新！');
      } else {
        await apiFetch('/content/banners', { method: 'POST', body: JSON.stringify(payload) });
        showToast('Banner已添加！');
      }
      await loadBanners();
      setBannerForm({
        section_key: '',
        title_zh: '',
        title_vi: '',
        content_zh: '',
        content_vi: '',
        image_url: '',
        button_text_zh: '',
        button_text_vi: '',
        sort_order: 0,
        is_active: true,
      });
      setEditingBanner(null);
    } catch {
      showToast('保存失败', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteBanner = async (id: number) => {
    if (!confirm('确定要删除这个Banner吗？')) return;
    setActionLoading(true);
    try {
      await apiFetch(`/content/banners/${id}`, { method: 'DELETE' });
      showToast('Banner已删除！');
      await loadBanners();
    } catch {
      showToast('删除失败', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // 首页内容（公司简介/优势亮点）操作
  const loadHomepageSection = useCallback(async (sectionKey: string) => {
    try {
      const res = await apiFetch<{ success: boolean; data: any[] }>(`/content/banners/all?section_key=${sectionKey}`);
      const item = res.data?.[0] || {};
      if (sectionKey === 'company_intro') {
        setHomepageSectionData({ ...item });
        // 同时加载 features
        const feRes = await apiFetch<{ success: boolean; data: any[] }>('/content/banners/all?section_key=company_intro_features');
        const feItem = feRes.data?.[0];
        if (feItem?.content_zh) {
          try { setIntroFeaturesText(feItem.content_zh); } catch { setIntroFeaturesText('[]'); }
        }
      } else if (sectionKey === 'advantages') {
        setHomepageSectionData({ ...item });
        // 同时加载 stats
        const stRes = await apiFetch<{ success: boolean; data: any[] }>('/content/banners/all?section_key=company_stats');
        const stItem = stRes.data?.[0];
        if (stItem?.content_zh) setStatsText(stItem.content_zh);
      }
    } catch { /* 保留现有数据 */ }
  }, []);

  const handleSaveHomepageSection = async () => {
    setActionLoading(true);
    try {
      if (homepageSubTab === 'company_intro') {
        // 保存 company_intro
        const payload = {
          section_key: 'company_intro',
          title_zh: homepageSectionData.title_zh || '',
          title_vi: homepageSectionData.title_vi || '',
          content_zh: homepageSectionData.content_zh || '',
          content_vi: homepageSectionData.content_vi || '',
          image_url: homepageSectionData.image_url || '',
          is_active: 1,
          sort_order: 1,
        };
        const existing = await apiFetch<{ success: boolean; data: any[] }>('/content/banners/all?section_key=company_intro');
        if (existing.data?.length > 0) {
          await apiFetch(`/content/banners/${existing.data[0].id}`, { method: 'PUT', body: JSON.stringify(payload) });
        } else {
          await apiFetch('/content/banners', { method: 'POST', body: JSON.stringify(payload) });
        }
        // 保存 features
        try {
          JSON.parse(introFeaturesText);
        } catch {
          showToast('特色数据格式错误，请检查JSON', 'error');
          setActionLoading(false); return;
        }
        const fePayload = {
          section_key: 'company_intro_features',
          title_zh: '核心特色',
          title_vi: 'Đặc điểm cốt lõi',
          content_zh: introFeaturesText,
          content_vi: introFeaturesText,
          is_active: 1, sort_order: 2,
        };
        const feExisting = await apiFetch<{ success: boolean; data: any[] }>('/content/banners/all?section_key=company_intro_features');
        if (feExisting.data?.length > 0) {
          await apiFetch(`/content/banners/${feExisting.data[0].id}`, { method: 'PUT', body: JSON.stringify(fePayload) });
        } else {
          await apiFetch('/content/banners', { method: 'POST', body: JSON.stringify(fePayload) });
        }
        showToast('公司简介已更新！');
      } else if (homepageSubTab === 'advantages') {
        // 保存 advantages
        try { JSON.parse(advantagesText); } catch { showToast('优势数据格式错误，请检查JSON', 'error'); setActionLoading(false); return; }
        try { JSON.parse(statsText); } catch { showToast('统计数据格式错误，请检查JSON', 'error'); setActionLoading(false); return; }
        const advPayload = {
          section_key: 'advantages',
          title_zh: homepageSectionData.title_zh || '',
          title_vi: homepageSectionData.title_vi || '',
          content_zh: advantagesText,
          content_vi: advantagesText,
          is_active: 1, sort_order: 3,
        };
        const advExisting = await apiFetch<{ success: boolean; data: any[] }>('/content/banners/all?section_key=advantages');
        if (advExisting.data?.length > 0) {
          await apiFetch(`/content/banners/${advExisting.data[0].id}`, { method: 'PUT', body: JSON.stringify(advPayload) });
        } else {
          await apiFetch('/content/banners', { method: 'POST', body: JSON.stringify(advPayload) });
        }
        const stPayload = {
          section_key: 'company_stats',
          title_zh: '公司数据', title_vi: 'Số liệu công ty',
          content_zh: statsText, content_vi: statsText,
          is_active: 1, sort_order: 4,
        };
        const stExisting = await apiFetch<{ success: boolean; data: any[] }>('/content/banners/all?section_key=company_stats');
        if (stExisting.data?.length > 0) {
          await apiFetch(`/content/banners/${stExisting.data[0].id}`, { method: 'PUT', body: JSON.stringify(stPayload) });
        } else {
          await apiFetch('/content/banners', { method: 'POST', body: JSON.stringify(stPayload) });
        }
        showToast('优势亮点已更新！');
      }
      await loadHomepageSection(homepageSubTab);
    } catch (err) {
      showToast('保存失败', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveService = async (form: ServiceForm, id?: number) => {
    setActionLoading(true);
    try {
      const payload = {
        ...form,
        is_active: form.is_active ? 1 : 0
      };
      if (id) {
        await apiFetch(`/services/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
        showToast('服务已更新！');
      } else {
        await apiFetch('/services', { method: 'POST', body: JSON.stringify(payload) });
        showToast('服务已添加！');
      }
      await loadServices();
      setServiceForm({
        slug: '',
        name_zh: '',
        name_vi: '',
        icon_class: 'fa fa-cog',
        icon_color: '#1890ff',
        description_zh: '',
        description_vi: '',
        details_zh: '[]',
        details_vi: '[]',
        image_url: '',
        sort_order: 0,
        is_active: true,
      });
      setEditingService(null);
    } catch {
      showToast('保存失败', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteService = async (id: number) => {
    if (!confirm('确定要删除这个服务吗？')) return;
    setActionLoading(true);
    try {
      await apiFetch(`/services/${id}`, { method: 'DELETE' });
      showToast('服务已删除！');
      await loadServices();
    } catch {
      showToast('删除失败', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveBranch = async (form: BranchOfficeForm, id?: number) => {
    setActionLoading(true);
    try {
      const payload = { ...form, is_active: form.is_active ? 1 : 0 };
      if (id) {
        await apiFetch(`/branch-offices/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
        showToast('分公司已更新！');
      } else {
        await apiFetch('/branch-offices', { method: 'POST', body: JSON.stringify(payload) });
        showToast('分公司已添加！');
      }
      await loadBranches();
      setBranchForm(emptyBranchForm);
      setEditingBranch(null);
    } catch {
      showToast('保存失败', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteBranch = async (id: number) => {
    if (!confirm('确定要删除这个分公司吗？')) return;
    setActionLoading(true);
    try {
      await apiFetch(`/branch-offices/${id}`, { method: 'DELETE' });
      showToast('分公司已删除！');
      await loadBranches();
    } catch {
      showToast('删除失败', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([loadProjects(), loadMessages(), loadSettings(), loadBranches(), loadServices(), loadBanners()]).finally(() =>
      setLoading(false)
    );
  }, [loadProjects, loadMessages, loadSettings, loadBranches, loadServices, loadBanners]);

  // ===================== 统计数据 =====================
  const stats = {
    totalMessages: messages.length,
    unreadMessages: messages.filter(m => m.status === 'unread').length,
    totalProjects: projects.length,
    featuredProjects: projects.filter(p => p.is_featured).length,
    totalViews: projects.reduce((s, p) => s + (p.view_count || 0), 0),
  };

  // ===================== 消息操作 =====================
  const handleMarkMessageRead = async (id: number) => {
    try {
      await apiFetch(`/contacts/messages/${id}/read`, { method: 'PATCH' });
      setMessages(prev => prev.map(m => m.id === id ? { ...m, status: 'read' as const } : m));
    } catch {
      showToast('操作失败', 'error');
    }
  };

  const handleDeleteMessage = async (id: number) => {
    if (!window.confirm('确定删除这条消息？')) return;
    try {
      await apiFetch(`/contacts/messages/${id}`, { method: 'DELETE' });
      setMessages(prev => prev.filter(m => m.id !== id));
      showToast('消息已删除');
    } catch {
      showToast('删除失败', 'error');
    }
  };

  const handleMarkAllRead = async () => {
    const unread = messages.filter(m => m.status === 'unread');
    for (const m of unread) {
      await apiFetch(`/contacts/messages/${m.id}/read`, { method: 'PATCH' }).catch(() => {});
    }
    setMessages(prev => prev.map(m => ({ ...m, status: 'read' as const })));
    showToast('全部已标记为已读');
  };

  // ===================== 项目操作 =====================
  const handleAddProjectSubmit = async () => {
    if (!newProject.name_zh.trim() || !newProject.name_vi.trim() ||
        !newProject.description_zh.trim() || !newProject.description_vi.trim()) {
      showToast('请填写所有必填字段（中越文名称和描述）', 'error');
      return;
    }
    setActionLoading(true);
    try {
      await apiFetch('/content/projects', {
        method: 'POST',
        body: JSON.stringify({
          ...newProject,
          area_size: newProject.area_size ? Number(newProject.area_size) : null,
        }),
      });
      showToast('项目添加成功！');
      setShowAddModal(false);
      setNewProject(emptyProjectForm);
      await loadProjects();
    } catch (err: any) {
      showToast(err.message || '添加失败', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditProjectOpen = (project: Project) => {
    setEditingProject({
      id: project.id,
      name_zh: project.name_zh || '',
      name_vi: project.name_vi || '',
      description_zh: project.description_zh || '',
      description_vi: project.description_vi || '',
      location_zh: project.location_zh || '',
      location_vi: project.location_vi || '',
      project_type: project.project_type || '',
      area_size: project.area_size ? String(project.area_size) : '',
      cover_image_url: project.cover_image_url || '',
      status: project.status || 'completed',
      is_featured: Boolean(project.is_featured),
    });
    setShowEditModal(true);
  };

  const handleEditProjectSubmit = async () => {
    if (!editingProject) return;
    if (!editingProject.name_zh.trim() || !editingProject.name_vi.trim()) {
      showToast('项目名称不能为空', 'error');
      return;
    }
    setActionLoading(true);
    try {
      await apiFetch(`/content/projects/${editingProject.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...editingProject,
          area_size: editingProject.area_size ? Number(editingProject.area_size) : null,
        }),
      });
      showToast('项目更新成功！');
      setShowEditModal(false);
      setEditingProject(null);
      await loadProjects();
    } catch (err: any) {
      showToast(err.message || '更新失败', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteProject = async (id: number) => {
    if (!window.confirm('确定删除这个项目？此操作不可恢复！')) return;
    try {
      await apiFetch(`/content/projects/${id}`, { method: 'DELETE' });
      setProjects(prev => prev.filter(p => p.id !== id));
      showToast('项目已删除');
    } catch {
      showToast('删除失败', 'error');
    }
  };

  const handleToggleFeatured = async (project: Project) => {
    try {
      await apiFetch(`/content/projects/${project.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_featured: !project.is_featured }),
      });
      setProjects(prev => prev.map(p => p.id === project.id ? { ...p, is_featured: !p.is_featured } : p));
      showToast(project.is_featured ? '已取消精选' : '已设为精选');
    } catch {
      showToast('操作失败', 'error');
    }
  };

  // ===================== 网站设置操作 =====================
  const handleSaveSettings = async () => {
    setActionLoading(true);
    try {
      await apiFetch('/settings/website', {
        method: 'PUT',
        body: JSON.stringify(websiteSettings),
      });
      showToast('设置已保存！');
    } catch (err: any) {
      showToast(err.message || '保存失败', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // 修改密码
  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    if (!currentPassword || !newPassword) {
      showToast('请填写当前密码和新密码', 'error');
      return;
    }
    if (newPassword.length < 6) {
      showToast('新密码至少6位', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('两次输入的新密码不一致', 'error');
      return;
    }
    setActionLoading(true);
    try {
      await apiFetch('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      showToast('密码修改成功！');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err: any) {
      showToast(err.message || '修改密码失败', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin');
  };

  // ===================== 时间格式化 =====================
  // 数据库存的是UTC时间（无时区标识），需要加上Z后缀转为本地时间
  const formatDateTime = (dateStr: string) => {
    const utcStr = dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T') + 'Z';
    return new Date(utcStr).toLocaleString('zh-CN');
  };

  // ===================== 样式辅助 =====================
  const getStatusBadgeColor = (status: string) => {
    const map: Record<string, string> = {
      unread: 'bg-yellow-100 text-yellow-800',
      read: 'bg-blue-100 text-blue-800',
      replied: 'bg-green-100 text-green-800',
      draft: 'bg-gray-100 text-gray-800',
      completed: 'bg-green-100 text-green-800',
      archived: 'bg-purple-100 text-purple-800',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status: string) => {
    const map: Record<string, string> = {
      unread: '未读', read: '已读', replied: '已回复',
      draft: '草稿', completed: '已完成', archived: '已归档',
    };
    return map[status] || status;
  };

  // ===================== 项目表单组件（复用） =====================
  const renderProjectForm = (
    form: NewProjectForm,
    setForm: React.Dispatch<React.SetStateAction<any>>
  ) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">项目名称（中文）*</label>
          <input
            type="text"
            value={form.name_zh}
            onChange={e => setForm((p: any) => ({ ...p, name_zh: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="如：北宁工业园电气工程"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">项目名称（越文）*</label>
          <input
            type="text"
            value={form.name_vi}
            onChange={e => setForm((p: any) => ({ ...p, name_vi: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="Tên dự án tiếng Việt"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">项目描述（中文）*</label>
          <textarea
            rows={3}
            value={form.description_zh}
            onChange={e => setForm((p: any) => ({ ...p, description_zh: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="项目简介（中文）"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">项目描述（越文）*</label>
          <textarea
            rows={3}
            value={form.description_vi}
            onChange={e => setForm((p: any) => ({ ...p, description_vi: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="Mô tả dự án tiếng Việt"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">项目地点（中文）</label>
          <input
            type="text"
            value={form.location_zh}
            onChange={e => setForm((p: any) => ({ ...p, location_zh: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="如：北宁省, 越南"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">项目地点（越文）</label>
          <input
            type="text"
            value={form.location_vi}
            onChange={e => setForm((p: any) => ({ ...p, location_vi: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="Địa điểm"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">项目分类</label>
          <select
            value={form.project_type}
            onChange={e => setForm((p: any) => ({ ...p, project_type: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">请选择分类</option>
            {Object.entries(PROJECT_TYPES).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">面积（㎡）</label>
          <input
            type="number"
            value={form.area_size}
            onChange={e => setForm((p: any) => ({ ...p, area_size: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="如：50000"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">封面图片</label>
        <div className="space-y-3">
          {/* 图片预览 */}
          {form.cover_image_url && (
            <div className="relative w-full max-w-xs">
              <img 
                src={form.cover_image_url.startsWith('http') ? form.cover_image_url : form.cover_image_url}
                alt="封面预览"
                className="w-full h-32 object-cover rounded-lg border"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x200?text=图片加载失败';
                }}
              />
              <button
                type="button"
                onClick={() => setForm((p: any) => ({ ...p, cover_image_url: '' }))}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
              >
                ×
              </button>
            </div>
          )}
          {/* 上传区域 */}
          <div className="flex items-center gap-3">
            <label className="flex-1 cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  
                  const formData = new FormData();
                  formData.append('image', file);
                  formData.append('category', 'projects');
                  formData.append('isPublic', 'true');
                  
                  try {
                    const token = localStorage.getItem('adminToken');
                    const res = await fetch('/api/images/upload', {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}` },
                      body: formData,
                    });
                    const data = await res.json();
                    if (data.success && data.data?.image?.url) {
                      setForm((p: any) => ({ ...p, cover_image_url: data.data.image.url }));
                    } else {
                      showToast(data.message || '上传失败', 'error');
                    }
                  } catch (err) {
                    showToast('上传失败，请重试', 'error');
                  }
                  e.target.value = '';
                }}
                className="hidden"
              />
              <div className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm hover:bg-blue-100 transition-colors flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {form.cover_image_url ? '更换图片' : '上传图片'}
              </div>
            </label>
            <span className="text-xs text-gray-400">或</span>
            <input
              type="text"
              value={form.cover_image_url}
              onChange={e => setForm((p: any) => ({ ...p, cover_image_url: e.target.value }))}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              placeholder="或输入图片URL"
            />
          </div>
          <p className="text-xs text-gray-400">支持 JPG、PNG、GIF、WebP，最大 10MB</p>
        </div>
      </div>

      <div className="flex items-center gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">项目状态</label>
          <div className="flex gap-4">
            {['completed', 'draft', 'archived'].map(s => (
              <label key={s} className="flex items-center gap-1 cursor-pointer">
                <input
                  type="radio"
                  name="projectStatus"
                  value={s}
                  checked={form.status === s}
                  onChange={e => setForm((p: any) => ({ ...p, status: e.target.value }))}
                  className="text-blue-600"
                />
                <span className="text-sm">{getStatusText(s)}</span>
              </label>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer mt-4">
          <input
            type="checkbox"
            checked={form.is_featured}
            onChange={e => setForm((p: any) => ({ ...p, is_featured: e.target.checked }))}
            className="h-4 w-4 text-blue-600 rounded"
          />
          <span className="text-sm font-medium text-gray-700">设为精选项目</span>
        </label>
      </div>
    </div>
  );

  // ===================== 渲染 =====================
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast提示 */}
      {toast && (
        <div className={cn(
          'fixed top-4 right-4 z-[100] px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium transition-all',
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-500'
        )}>
          {toast.msg}
        </div>
      )}

      {/* 添加项目模态框 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-800">添加新项目</h3>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {renderProjectForm(newProject, setNewProject)}
              <div className="pt-4 border-t mt-4 flex justify-end space-x-3">
                <button
                  onClick={() => { setShowAddModal(false); setNewProject(emptyProjectForm); }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >取消</button>
                <button
                  onClick={handleAddProjectSubmit}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                >
                  {actionLoading ? '提交中...' : '添加项目'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 编辑项目模态框 */}
      {showEditModal && editingProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-800">编辑项目</h3>
                <button onClick={() => { setShowEditModal(false); setEditingProject(null); }} className="text-gray-400 hover:text-gray-600">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {renderProjectForm(editingProject, setEditingProject)}
              <div className="pt-4 border-t mt-4 flex justify-end space-x-3">
                <button
                  onClick={() => { setShowEditModal(false); setEditingProject(null); }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >取消</button>
                <button
                  onClick={handleEditProjectSubmit}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                >
                  {actionLoading ? '保存中...' : '保存修改'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 顶部导航栏 */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg overflow-hidden">
                <img src="/logo.jpg" alt="Logo" className="w-full h-full object-cover" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">惠亚建筑 CMS</h1>
                <p className="text-sm text-gray-500">后台管理系统</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center text-gray-600 hover:text-gray-900 text-sm"
            >
              <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              退出登录
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* 侧边栏 */}
          <aside className="lg:w-56">
            <nav className="bg-white rounded-xl shadow-sm p-4">
              <div className="space-y-1">
                {([
                  { key: 'dashboard', label: '仪表板', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' },
                  { key: 'messages', label: '客户消息', icon: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z', badge: stats.unreadMessages },
                  { key: 'projects', label: '项目管理', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
                  { key: 'services', label: '服务管理', icon: 'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9', badge: services.length },
                  { key: 'homepage', label: '首页管理', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
                  { key: 'branches', label: '分公司管理', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', badge: branchOffices.length },
                  { key: 'settings', label: '系统设置', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
                ] as any[]).map(item => (
                  <button
                    key={item.key}
                    onClick={() => setActiveTab(item.key)}
                    className={cn(
                      'w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center',
                      activeTab === item.key ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                    )}
                  >
                    <svg className="h-5 w-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    {item.label}
                    {item.badge ? (
                      <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{item.badge}</span>
                    ) : null}
                  </button>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t">
                <p className="text-xs font-medium text-gray-500 px-4 mb-2">快速操作</p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded"
                >
                  + 添加新项目
                </button>
              </div>
            </nav>
          </aside>

          {/* 主内容 */}
          <main className="flex-1">
            {loading ? (
              <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-gray-600">加载数据中...</p>
              </div>
            ) : activeTab === 'dashboard' ? (
              /* ===== 仪表板 ===== */
              <div>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">欢迎回来，管理员</h2>
                  <p className="text-gray-500 text-sm mt-1">{new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  {[
                    { label: '客户消息', value: stats.totalMessages, sub: `未读: ${stats.unreadMessages}`, color: 'blue' },
                    { label: '项目总数', value: stats.totalProjects, sub: `精选: ${stats.featuredProjects}`, color: 'green' },
                    { label: '总浏览量', value: stats.totalViews, sub: '累计', color: 'purple' },
                  ].map(card => (
                    <div key={card.label} className="bg-white rounded-xl p-6 shadow-sm border">
                      <p className="text-sm text-gray-500">{card.label}</p>
                      <p className="text-3xl font-bold text-gray-800 mt-1">{card.value}</p>
                      <p className="text-sm text-gray-500 mt-2">{card.sub}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 最近消息 */}
                  <div className="bg-white rounded-xl shadow-sm border p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-gray-800">最近消息</h3>
                      <button onClick={() => setActiveTab('messages')} className="text-sm text-blue-600">查看全部 →</button>
                    </div>
                    {messages.length === 0 ? (
                      <p className="text-gray-400 text-sm text-center py-4">暂无消息</p>
                    ) : (
                      <div className="space-y-3">
                        {messages.slice(0, 3).map(msg => (
                          <div key={msg.id} className="p-3 border rounded-lg">
                            <div className="flex justify-between">
                              <span className="font-medium text-sm text-gray-800">{msg.name}</span>
                              <span className={cn('text-xs px-2 py-0.5 rounded-full', getStatusBadgeColor(msg.status))}>{getStatusText(msg.status)}</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1 truncate">{msg.subject}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 最近项目 */}
                  <div className="bg-white rounded-xl shadow-sm border p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-gray-800">最近项目</h3>
                      <button onClick={() => setActiveTab('projects')} className="text-sm text-blue-600">查看全部 →</button>
                    </div>
                    {projects.length === 0 ? (
                      <p className="text-gray-400 text-sm text-center py-4">暂无项目</p>
                    ) : (
                      <div className="space-y-3">
                        {projects.slice(0, 3).map(proj => (
                          <div key={proj.id} className="p-3 border rounded-lg">
                            <div className="flex justify-between">
                              <span className="font-medium text-sm text-gray-800 truncate">{proj.name_zh}</span>
                              <span className={cn('text-xs px-2 py-0.5 rounded-full ml-2 shrink-0', getStatusBadgeColor(proj.status))}>{getStatusText(proj.status)}</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{PROJECT_TYPES[proj.project_type] || proj.project_type} · {proj.location_zh}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

            ) : activeTab === 'messages' ? (
              /* ===== 消息管理 ===== */
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="p-6 border-b flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">客户消息管理</h2>
                    <p className="text-gray-500 text-sm mt-1">管理客户通过网站提交的咨询消息</p>
                  </div>
                  {stats.unreadMessages > 0 && (
                    <button onClick={handleMarkAllRead} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                      标记全部已读
                    </button>
                  )}
                </div>
                {messages.length === 0 ? (
                  <div className="py-16 text-center text-gray-400">暂无客户消息</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          {['客户信息', '主题', '状态', '时间', '操作'].map(h => (
                            <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {messages.map(msg => (
                          <tr key={msg.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div className="font-medium text-gray-900 text-sm">{msg.name}</div>
                              <div className="text-xs text-gray-500">{msg.email}</div>
                              {msg.phone && <div className="text-xs text-gray-400">{msg.phone}</div>}
                            </td>
                            <td className="px-6 py-4 max-w-xs">
                              <div className="font-medium text-sm text-gray-900 truncate">{msg.subject}</div>
                              <div className="text-xs text-gray-500 line-clamp-2">{msg.message}</div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={cn('text-xs px-3 py-1 rounded-full', getStatusBadgeColor(msg.status))}>
                                {getStatusText(msg.status)}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-xs text-gray-500">{formatDateTime(msg.created_at)}</td>
                            <td className="px-6 py-4">
                              <div className="flex gap-2">
                                <button onClick={() => setSelectedMessage(msg)} className="text-xs text-blue-600 hover:underline">查看</button>
                                {msg.status === 'unread' && (
                                  <button onClick={() => handleMarkMessageRead(msg.id)} className="text-xs text-green-600 hover:underline">标记已读</button>
                                )}
                                <button onClick={() => handleDeleteMessage(msg.id)} className="text-xs text-red-600 hover:underline">删除</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

            ) : activeTab === 'projects' ? (
              /* ===== 项目管理 ===== */
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="p-6 border-b flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">项目管理</h2>
                    <p className="text-gray-500 text-sm mt-1">管理网站展示的项目案例（共 {projects.length} 个）</p>
                  </div>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-1"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    添加新项目
                  </button>
                </div>
                {projects.length === 0 ? (
                  <div className="py-16 text-center text-gray-400">
                    <p>暂无项目，点击"添加新项目"开始</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          {['项目名称', '分类', '地点', '浏览量', '精选', '状态', '操作'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {projects.map(proj => (
                          <tr key={proj.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4">
                              <div className="font-medium text-gray-900 text-sm max-w-[200px] truncate">{proj.name_zh}</div>
                              <div className="text-xs text-gray-400 truncate">{proj.name_vi}</div>
                            </td>
                            <td className="px-4 py-4">
                              <span className="text-xs px-2 py-1 bg-gray-100 rounded-full">
                                {PROJECT_TYPES[proj.project_type] || proj.project_type || '—'}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-500 max-w-[120px] truncate">{proj.location_zh}</td>
                            <td className="px-4 py-4 text-sm text-gray-600">{proj.view_count}</td>
                            <td className="px-4 py-4">
                              <button
                                onClick={() => handleToggleFeatured(proj)}
                                className={cn('text-xs px-2 py-1 rounded-full transition-colors', proj.is_featured ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200')}
                              >
                                {proj.is_featured ? '★ 精选' : '☆ 普通'}
                              </button>
                            </td>
                            <td className="px-4 py-4">
                              <span className={cn('text-xs px-2 py-1 rounded-full', getStatusBadgeColor(proj.status))}>
                                {getStatusText(proj.status)}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex gap-2">
                                <button onClick={() => handleEditProjectOpen(proj)} className="text-xs text-blue-600 hover:underline">编辑</button>
                                <button onClick={() => handleDeleteProject(proj.id)} className="text-xs text-red-600 hover:underline">删除</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

            ) : activeTab === 'settings' ? (
              /* ===== 系统设置 ===== */
              <div className="bg-white rounded-xl shadow-sm border p-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">系统设置</h2>
                <div className="max-w-2xl space-y-6">
                  <div className="border rounded-lg p-6">
                    <h3 className="font-semibold text-gray-800 mb-4">网站设置</h3>
                    <div className="space-y-4">
                      {[
                        { key: 'site_name_zh', label: '公司名称（中文）' },
                        { key: 'site_name_vi', label: '公司名称（越文）' },
                        { key: 'site_logo_url', label: 'Logo URL' },
                        { key: 'contact_phone', label: '联系电话' },
                        { key: 'contact_email', label: '联系邮箱' },
                        { key: 'contact_address_zh', label: '公司地址（中文）' },
                        { key: 'contact_address_vi', label: '公司地址（越文）' },
                        { key: 'working_hours_zh', label: '工作时间（中文）' },
                      ].map(field => (
                        <div key={field.key}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                          <input
                            type="text"
                            value={websiteSettings[field.key] || ''}
                            onChange={e => setWebsiteSettings(prev => ({ ...prev, [field.key]: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      ))}
                      <button
                        onClick={handleSaveSettings}
                        disabled={actionLoading}
                        className="mt-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                      >
                        {actionLoading ? '保存中...' : '保存设置'}
                      </button>
                    </div>

                    {/* 修改密码 */}
                    <div className="border rounded-lg p-6">
                      <h3 className="font-semibold text-gray-800 mb-4">修改密码</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">当前密码</label>
                          <input
                            type="password"
                            value={passwordForm.currentPassword}
                            onChange={e => setPasswordForm(p => ({ ...p, currentPassword: e.target.value }))}
                            placeholder="请输入当前密码"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">新密码</label>
                          <input
                            type="password"
                            value={passwordForm.newPassword}
                            onChange={e => setPasswordForm(p => ({ ...p, newPassword: e.target.value }))}
                            placeholder="至少6位"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">确认新密码</label>
                          <input
                            type="password"
                            value={passwordForm.confirmPassword}
                            onChange={e => setPasswordForm(p => ({ ...p, confirmPassword: e.target.value }))}
                            placeholder="再次输入新密码"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <button
                          onClick={handleChangePassword}
                          disabled={actionLoading}
                          className="mt-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60"
                        >
                          {actionLoading ? '修改中...' : '修改密码'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : activeTab === 'services' ? (
              /* ===== 服务管理 ===== */
              <div className="bg-white rounded-xl shadow-sm border p-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">服务管理</h2>
                
                {/* 添加/编辑表单 */}
                <div className="border rounded-lg p-6 mb-6">
                  <h3 className="font-semibold text-gray-800 mb-4">{editingService ? '编辑服务' : '添加服务'}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">服务名称（中文）*</label>
                      <input
                        type="text"
                        value={serviceForm.name_zh}
                        onChange={e => setServiceForm(prev => ({ ...prev, name_zh: e.target.value }))}
                        placeholder="如：电气工程"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">服务名称（越文）*</label>
                      <input
                        type="text"
                        value={serviceForm.name_vi}
                        onChange={e => setServiceForm(prev => ({ ...prev, name_vi: e.target.value }))}
                        placeholder="如：Công trình điện"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">图标CSS类名</label>
                      <input
                        type="text"
                        value={serviceForm.icon_class}
                        onChange={e => setServiceForm(prev => ({ ...prev, icon_class: e.target.value }))}
                        placeholder="如：fa fa-bolt"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">服务图片</label>
                      <div className="space-y-3">
                        {/* 图片预览 */}
                        {serviceForm.image_url && (
                          <div className="relative w-full max-w-xs">
                            <img 
                              src={serviceForm.image_url.startsWith('http') ? serviceForm.image_url : serviceForm.image_url}
                              alt="服务图片预览"
                              className="w-full h-32 object-cover rounded-lg border"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x200?text=图片加载失败';
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => setServiceForm(prev => ({ ...prev, image_url: '' }))}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                            >
                              ×
                            </button>
                          </div>
                        )}
                        {/* 上传区域 */}
                        <div className="flex items-center gap-3">
                          <label className="flex-1 cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                
                                const formData = new FormData();
                                formData.append('image', file);
                                formData.append('category', 'services');
                                formData.append('isPublic', 'true');
                                
                                try {
                                  const token = localStorage.getItem('adminToken');
                                  const res = await fetch('/api/images/upload', {
                                    method: 'POST',
                                    headers: { Authorization: `Bearer ${token}` },
                                    body: formData,
                                  });
                                  const data = await res.json();
                                  if (data.success && data.data?.image?.url) {
                                    setServiceForm(prev => ({ ...prev, image_url: data.data.image.url }));
                                  } else {
                                    showToast(data.message || '上传失败', 'error');
                                  }
                                } catch (err) {
                                  showToast('上传失败，请重试', 'error');
                                }
                                e.target.value = '';
                              }}
                              className="hidden"
                            />
                            <div className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm hover:bg-blue-100 transition-colors flex items-center gap-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              {serviceForm.image_url ? '更换图片' : '上传图片'}
                            </div>
                          </label>
                          <span className="text-xs text-gray-400">或</span>
                          <input
                            type="text"
                            value={serviceForm.image_url}
                            onChange={e => setServiceForm(prev => ({ ...prev, image_url: e.target.value }))}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                            placeholder="或输入图片URL"
                          />
                        </div>
                        <p className="text-xs text-gray-400">支持 JPG、PNG、GIF、WebP，最大 10MB</p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">简介（中文）*</label>
                      <textarea
                        rows={2}
                        value={serviceForm.description_zh}
                        onChange={e => setServiceForm(prev => ({ ...prev, description_zh: e.target.value }))}
                        placeholder="服务简介（中文）"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">简介（越文）*</label>
                      <textarea
                        rows={2}
                        value={serviceForm.description_vi}
                        onChange={e => setServiceForm(prev => ({ ...prev, description_vi: e.target.value }))}
                        placeholder="Mô tả dịch vụ (Tiếng Việt)"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">服务详情（中文，每行一项）</label>
                      <textarea
                        rows={4}
                        value={(() => { try { return JSON.parse(serviceForm.details_zh || '[]').join('\n'); } catch { return ''; } })()}
                        onChange={e => setServiceForm(prev => ({ ...prev, details_zh: JSON.stringify(e.target.value.split('\n').filter(s => s.trim())) }))}
                        placeholder="工业配电系统设计与安装&#10;PLC控制系统编程与调试&#10;工业照明系统优化"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 font-mono"
                      />
                      <p className="text-xs text-gray-400 mt-1">每行一项，保存为JSON数组</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">服务详情（越文，每行一项）</label>
                      <textarea
                        rows={4}
                        value={(() => { try { return JSON.parse(serviceForm.details_vi || '[]').join('\n'); } catch { return ''; } })()}
                        onChange={e => setServiceForm(prev => ({ ...prev, details_vi: JSON.stringify(e.target.value.split('\n').filter(s => s.trim())) }))}
                        placeholder="Thiết kế và lắp đặt hệ thống phân phối điện công nghiệp&#10;Lập trình và vận hành hệ thống điều khiển PLC"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 font-mono"
                      />
                      <p className="text-xs text-gray-400 mt-1">Mỗi dòng một mục, lưu dưới dạng mảng JSON</p>
                    </div>
                    <div></div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">排序</label>
                      <input
                        type="number"
                        value={serviceForm.sort_order}
                        onChange={e => setServiceForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex items-center pt-6">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={serviceForm.is_active}
                          onChange={e => setServiceForm(prev => ({ ...prev, is_active: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">启用状态</span>
                      </label>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => handleSaveService(serviceForm, editingService?.id)}
                      disabled={actionLoading || !serviceForm.name_zh || !serviceForm.name_vi}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                    >
                      {actionLoading ? '保存中...' : editingService ? '更新' : '添加'}
                    </button>
                    {editingService && (
                      <button
                        onClick={() => { 
                          setEditingService(null); 
                          setServiceForm({
                            slug: '',
                            name_zh: '',
                            name_vi: '',
                            icon_class: 'fa fa-cog',
                            icon_color: '#1890ff',
                            description_zh: '',
                            description_vi: '',
                            details_zh: '[]',
                            details_vi: '[]',
                            image_url: '',
                            sort_order: 0,
                            is_active: true,
                          }); 
                        }}
                        className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                      >
                        取消
                      </button>
                    )}
                  </div>
                </div>
                
                {/* 服务列表 */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4">服务列表（{services.length}个）</h3>
                  {services.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">暂无服务，请添加</p>
                  ) : (
                    <div className="space-y-4">
                      {services.map(service => (
                        <div key={service.id} className="border rounded-lg p-4 flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm text-gray-400 font-mono">{service.icon_class}</span>
                              <h4 className="font-semibold text-gray-800">{service.name_zh}</h4>
                              <span className="text-sm text-gray-500">/ {service.name_vi}</span>
                              {!service.is_active && (
                                <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">已禁用</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-1">📝 {service.description_zh}</p>
                            {service.image_url && (
                              <p className="text-sm text-gray-500">🖼️ {service.image_url}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-1">排序: {service.sort_order}</p>
                            <p className="text-xs text-gray-400">详情: {(service as any).details_zh ? JSON.parse((service as any).details_zh).length + '项' : '0项'}</p>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => {
                                setEditingService(service);
                                setServiceForm({
                                  slug: service.slug || '',
                                  name_zh: service.name_zh,
                                  name_vi: service.name_vi,
                                  icon_class: service.icon_class || 'fa fa-cog',
                                  icon_color: service.icon_color || '#1890ff',
                                  description_zh: service.description_zh || '',
                                  description_vi: service.description_vi || '',
                                  details_zh: (service as any).details_zh || '[]',
                                  details_vi: (service as any).details_vi || '[]',
                                  image_url: service.image_url || '',
                                  sort_order: service.sort_order || 0,
                                  is_active: !!service.is_active,
                                });
                              }}
                              className="px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                            >
                              编辑
                            </button>
                            <button
                              onClick={() => handleDeleteService(service.id)}
                              className="px-3 py-1 text-sm bg-red-50 text-red-600 rounded hover:bg-red-100"
                            >
                              删除
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : activeTab === 'homepage' ? (
              /* ===== 首页管理 ===== */
              <div className="bg-white rounded-xl shadow-sm border p-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">首页内容管理</h2>
                
                {/* 子导航 */}
                <div className="flex gap-2 mb-6 border-b pb-3">
                  {['banners', 'company_intro', 'advantages'].map(key => (
                    <button
                      key={key}
                      onClick={() => {
                        setHomepageSubTab(key as any);
                        if (key === 'banners') loadBanners();
                        if (key === 'company_intro' || key === 'advantages') loadHomepageSection(key);
                      }}
                      className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                        homepageSubTab === key ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {key === 'banners' ? 'Banner管理' : key === 'company_intro' ? '公司简介' : '优势亮点'}
                    </button>
                  ))}
                </div>

                {homepageSubTab === 'banners' && (
                  <>
                {/* 添加/编辑表单 */}
                <div className="border rounded-lg p-6 mb-6">
                  <h3 className="font-semibold text-gray-800 mb-4">{editingBanner ? '编辑Banner' : '添加Banner'}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">标题（中文）*</label>
                      <input
                        type="text"
                        value={bannerForm.title_zh}
                        onChange={e => setBannerForm(prev => ({ ...prev, title_zh: e.target.value }))}
                        placeholder="如：专业机电工程解决方案"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">标题（越文）*</label>
                      <input
                        type="text"
                        value={bannerForm.title_vi}
                        onChange={e => setBannerForm(prev => ({ ...prev, title_vi: e.target.value }))}
                        placeholder="Giải pháp kỹ thuật cơ điện"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">副标题（中文）*</label>
                      <textarea
                        rows={2}
                        value={bannerForm.content_zh}
                        onChange={e => setBannerForm(prev => ({ ...prev, content_zh: e.target.value }))}
                        placeholder="首页轮播图的副标题描述（中文）"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">副标题（越文）*</label>
                      <textarea
                        rows={2}
                        value={bannerForm.content_vi}
                        onChange={e => setBannerForm(prev => ({ ...prev, content_vi: e.target.value }))}
                        placeholder="Mô tả phụ đề banner (Tiếng Việt)"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    {/* Banner图片上传 */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Banner背景图片</label>
                      <div className="space-y-3">
                        {bannerForm.image_url && (
                          <div className="relative w-full max-w-sm">
                            <img 
                              src={bannerForm.image_url.startsWith('http') ? bannerForm.image_url : bannerForm.image_url}
                              alt="Banner预览"
                              className="w-full h-40 object-cover rounded-lg border"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/600x300?text=图片加载失败';
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => setBannerForm(prev => ({ ...prev, image_url: '' }))}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                            >
                              ×
                            </button>
                          </div>
                        )}
                        <div className="flex items-center gap-3">
                          <label className="flex-1 cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                
                                const formData = new FormData();
                                formData.append('image', file);
                                formData.append('category', 'banners');
                                formData.append('isPublic', 'true');
                                
                                try {
                                  const token = localStorage.getItem('adminToken');
                                  const res = await fetch('/api/images/upload', {
                                    method: 'POST',
                                    headers: { Authorization: `Bearer ${token}` },
                                    body: formData,
                                  });
                                  const data = await res.json();
                                  if (data.success && data.data?.image?.url) {
                                    setBannerForm(prev => ({ ...prev, image_url: data.data.image.url }));
                                  } else {
                                    showToast(data.message || '上传失败', 'error');
                                  }
                                } catch (err) {
                                  showToast('上传失败，请重试', 'error');
                                }
                                e.target.value = '';
                              }}
                              className="hidden"
                            />
                            <div className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm hover:bg-blue-100 transition-colors flex items-center gap-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              {bannerForm.image_url ? '更换图片' : '上传Banner图片'}
                            </div>
                          </label>
                          <span className="text-xs text-gray-400">或</span>
                          <input
                            type="text"
                            value={bannerForm.image_url}
                            onChange={e => setBannerForm(prev => ({ ...prev, image_url: e.target.value }))}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                            placeholder="或输入图片URL"
                          />
                        </div>
                        <p className="text-xs text-gray-400">建议尺寸：1920×800像素，支持 JPG、PNG，最大 10MB</p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">按钮文字（中文）</label>
                      <input
                        type="text"
                        value={bannerForm.button_text_zh}
                        onChange={e => setBannerForm(prev => ({ ...prev, button_text_zh: e.target.value }))}
                        placeholder="如：了解我们的服务"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">按钮文字（越文）</label>
                      <input
                        type="text"
                        value={bannerForm.button_text_vi}
                        onChange={e => setBannerForm(prev => ({ ...prev, button_text_vi: e.target.value }))}
                        placeholder="Tìm hiểu dịch vụ"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">排序</label>
                      <input
                        type="number"
                        value={bannerForm.sort_order}
                        onChange={e => setBannerForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex items-center pt-6">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={bannerForm.is_active}
                          onChange={e => setBannerForm(prev => ({ ...prev, is_active: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">启用状态</span>
                      </label>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => handleSaveBanner(bannerForm, editingBanner?.id)}
                      disabled={actionLoading}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                    >
                      {actionLoading ? '保存中...' : editingBanner ? '更新' : '添加'}
                    </button>
                    {editingBanner && (
                      <button
                        onClick={() => { 
                          setEditingBanner(null); 
                          setBannerForm({
                            section_key: '',
                            title_zh: '',
                            title_vi: '',
                            content_zh: '',
                            content_vi: '',
                            image_url: '',
                            button_text_zh: '',
                            button_text_vi: '',
                            sort_order: 0,
                            is_active: true,
                          }); 
                        }}
                        className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                      >
                        取消
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Banner列表 */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4">Banner列表（{banners.length}个）</h3>
                  {banners.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">暂无Banner，请添加</p>
                  ) : (
                    <div className="space-y-4">
                      {banners.map((banner) => (
                        <div key={banner.id} className="border rounded-lg p-4 flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-semibold text-gray-800">{banner.title_zh}</h4>
                              <span className="text-sm text-gray-500">/ {banner.title_vi}</span>
                              {!banner.is_active && (
                                <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">已禁用</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-1">📝 {banner.content_zh}</p>
                            {banner.image_url && (
                              <p className="text-xs text-gray-500 mb-1">🖼️ 图片已设置</p>
                            )}
                            <p className="text-xs text-gray-400 mt-1">排序: {banner.sort_order}</p>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => {
                                setEditingBanner(banner);
                                setBannerForm({
                                  id: banner.id,
                                  section_key: banner.section_key || '',
                                  title_zh: banner.title_zh,
                                  title_vi: banner.title_vi,
                                  content_zh: banner.content_zh,
                                  content_vi: banner.content_vi,
                                  image_url: banner.image_url || '',
                                  button_text_zh: banner.button_text_zh || '',
                                  button_text_vi: banner.button_text_vi || '',
                                  sort_order: banner.sort_order || 0,
                                  is_active: !!banner.is_active,
                                });
                              }}
                              className="px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                            >
                              编辑
                            </button>
                            <button
                              onClick={() => handleDeleteBanner(banner.id)}
                              className="px-3 py-1 text-sm bg-red-50 text-red-600 rounded hover:bg-red-100"
                            >
                              删除
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {homepageSubTab === 'company_intro' && (
              <div>
                <div className="border rounded-lg p-6 mb-6">
                  <h3 className="font-semibold text-gray-800 mb-4">公司简介编辑</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">标题（中文）</label>
                      <input type="text" value={homepageSectionData.title_zh || ''}
                        onChange={e => setHomepageSectionData((p: any) => ({...p, title_zh: e.target.value}))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">标题（越文）</label>
                      <input type="text" value={homepageSectionData.title_vi || ''}
                        onChange={e => setHomepageSectionData((p: any) => ({...p, title_vi: e.target.value}))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">描述内容（中文）</label>
                      <textarea rows={6} value={homepageSectionData.content_zh || ''}
                        onChange={e => setHomepageSectionData((p: any) => ({...p, content_zh: e.target.value}))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">描述内容（越文）</label>
                      <textarea rows={6} value={homepageSectionData.content_vi || ''}
                        onChange={e => setHomepageSectionData((p: any) => ({...p, content_vi: e.target.value}))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">公司图片URL</label>
                      <input type="text" value={homepageSectionData.image_url || ''}
                        onChange={e => setHomepageSectionData((p: any) => ({...p, image_url: e.target.value}))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">特色功能（JSON格式）</label>
                      <textarea rows={8} value={introFeaturesText}
                        onChange={e => setIntroFeaturesText(e.target.value)}
                        placeholder='[{"title_zh":"专业服务","title_vi":"Dịch vụ chuyên nghiệp","description_zh":"..","description_vi":".."}]'
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-mono" />
                      <p className="text-xs text-gray-400 mt-1">每个卡片需要：title_zh, title_vi, description_zh, description_vi</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <button onClick={handleSaveHomepageSection} disabled={actionLoading}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60">
                      {actionLoading ? '保存中...' : '保存公司简介'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {homepageSubTab === 'advantages' && (
              <div>
                <div className="border rounded-lg p-6 mb-6">
                  <h3 className="font-semibold text-gray-800 mb-4">优势亮点编辑</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">优势列表（JSON格式）</label>
                      <textarea rows={12} value={advantagesText}
                        onChange={e => setAdvantagesText(e.target.value)}
                        placeholder='[{"icon":"🏭","title_zh":"工业厂房专业经验","title_vi":"...","desc_zh":"...","desc_vi":"..."}]'
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-mono" />
                      <p className="text-xs text-gray-400 mt-1">每个优势需要：icon, title_zh, title_vi, desc_zh, desc_vi</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">统计数据（JSON格式，最多3项）</label>
                      <textarea rows={6} value={statsText}
                        onChange={e => setStatsText(e.target.value)}
                        placeholder='[{"label_zh":"完成项目","label_vi":"Dự án hoàn thành","value":"50+"}]'
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-mono" />
                      <p className="text-xs text-gray-400 mt-1">每项需要：label_zh, label_vi, value</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <button onClick={handleSaveHomepageSection} disabled={actionLoading}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60">
                      {actionLoading ? '保存中...' : '保存优势亮点'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            </div>
            ) : activeTab === 'branches' ? (
              /* ===== 分公司管理 ===== */
              <div className="bg-white rounded-xl shadow-sm border p-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">分公司管理</h2>
                
                {/* 添加/编辑表单 */}
                <div className="border rounded-lg p-6 mb-6">
                  <h3 className="font-semibold text-gray-800 mb-4">{editingBranch ? '编辑分公司' : '添加分公司'}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">城市名称（中文）*</label>
                      <input
                        type="text"
                        value={branchForm.city_zh}
                        onChange={e => setBranchForm(prev => ({ ...prev, city_zh: e.target.value }))}
                        placeholder="如：胡志明市"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">城市名称（越文）*</label>
                      <input
                        type="text"
                        value={branchForm.city_vi}
                        onChange={e => setBranchForm(prev => ({ ...prev, city_vi: e.target.value }))}
                        placeholder="如：Thành phố Hồ Chí Minh"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">地址（中文）*</label>
                      <input
                        type="text"
                        value={branchForm.address_zh}
                        onChange={e => setBranchForm(prev => ({ ...prev, address_zh: e.target.value }))}
                        placeholder="详细地址"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">地址（越文）*</label>
                      <input
                        type="text"
                        value={branchForm.address_vi}
                        onChange={e => setBranchForm(prev => ({ ...prev, address_vi: e.target.value }))}
                        placeholder="Địa chỉ chi tiết"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">联系电话</label>
                      <input
                        type="text"
                        value={branchForm.phone}
                        onChange={e => setBranchForm(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="+84 ..."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">邮箱</label>
                      <input
                        type="text"
                        value={branchForm.email}
                        onChange={e => setBranchForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="email@domain.com"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">排序</label>
                      <input
                        type="number"
                        value={branchForm.sort_order}
                        onChange={e => setBranchForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex items-center pt-6">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={branchForm.is_active}
                          onChange={e => setBranchForm(prev => ({ ...prev, is_active: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">启用状态</span>
                      </label>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => handleSaveBranch(branchForm, editingBranch?.id)}
                      disabled={actionLoading || !branchForm.city_zh || !branchForm.city_vi}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                    >
                      {actionLoading ? '保存中...' : editingBranch ? '更新' : '添加'}
                    </button>
                    {editingBranch && (
                      <button
                        onClick={() => { setEditingBranch(null); setBranchForm(emptyBranchForm); }}
                        className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                      >
                        取消
                      </button>
                    )}
                  </div>
                </div>
                
                {/* 分公司列表 */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4">分公司列表（{branchOffices.length}个）</h3>
                  {branchOffices.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">暂无分公司，请添加</p>
                  ) : (
                    <div className="space-y-4">
                      {branchOffices.map(branch => (
                        <div key={branch.id} className="border rounded-lg p-4 flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-semibold text-gray-800">{branch.city_zh}</h4>
                              <span className="text-sm text-gray-500">/ {branch.city_vi}</span>
                              {!branch.is_active && (
                                <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">已禁用</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">📍 {branch.address_zh}</p>
                            <p className="text-sm text-gray-600">📍 {branch.address_vi}</p>
                            {branch.phone && <p className="text-sm text-gray-600 mt-1">📞 {branch.phone}</p>}
                            {branch.email && <p className="text-sm text-gray-600">✉️ {branch.email}</p>}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => setEditingBranch(branch) || setBranchForm({
                                city_zh: branch.city_zh,
                                city_vi: branch.city_vi,
                                address_zh: branch.address_zh,
                                address_vi: branch.address_vi,
                                phone: branch.phone || '',
                                email: branch.email || '',
                                sort_order: branch.sort_order || 0,
                                is_active: !!branch.is_active,
                              })}
                              className="px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                            >
                              编辑
                            </button>
                            <button
                              onClick={() => handleDeleteBranch(branch.id)}
                              className="px-3 py-1 text-sm bg-red-50 text-red-600 rounded hover:bg-red-100"
                            >
                              删除
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </main>
        </div>
      </div>

      {/* 消息详情弹窗 */}
      {selectedMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setSelectedMessage(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">消息详情</h3>
              <button onClick={() => setSelectedMessage(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex gap-2"><span className="font-medium text-gray-600 w-20 shrink-0">姓名：</span><span className="text-gray-900">{selectedMessage.name}</span></div>
              <div className="flex gap-2"><span className="font-medium text-gray-600 w-20 shrink-0">邮箱：</span><span className="text-gray-900">{selectedMessage.email}</span></div>
              {selectedMessage.phone && <div className="flex gap-2"><span className="font-medium text-gray-600 w-20 shrink-0">电话：</span><span className="text-gray-900">{selectedMessage.phone}</span></div>}
              <div className="flex gap-2"><span className="font-medium text-gray-600 w-20 shrink-0">主题：</span><span className="text-gray-900">{selectedMessage.subject}</span></div>
              <div className="flex gap-2"><span className="font-medium text-gray-600 w-20 shrink-0">状态：</span><span className={cn('text-xs px-2 py-0.5 rounded-full', getStatusBadgeColor(selectedMessage.status))}>{getStatusText(selectedMessage.status)}</span></div>
              <div className="flex gap-2"><span className="font-medium text-gray-600 w-20 shrink-0">时间：</span><span className="text-gray-900">{formatDateTime(selectedMessage.created_at)}</span></div>
              <div>
                <span className="font-medium text-gray-600 block mb-1">留言内容：</span>
                <div className="bg-gray-50 rounded-lg p-3 text-gray-900 whitespace-pre-wrap leading-relaxed">{selectedMessage.message}</div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              {selectedMessage.status === 'unread' && (
                <button onClick={() => { handleMarkMessageRead(selectedMessage.id); setSelectedMessage(null); }} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">标记已读</button>
              )}
              <button onClick={() => setSelectedMessage(null)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">关闭</button>
            </div>
          </div>
        </div>
      )}

      <footer className="mt-12 py-6 bg-white border-t text-center text-sm text-gray-400">
        © 2026 惠亚建筑工程有限公司 · 版本 1.0.0 · 最后更新: {new Date().toLocaleDateString('zh-CN')}
      </footer>
    </div>
  );
};

export default AdminDashboard;
